import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Modal from '../components/Modal';
import FamilyModal from '../components/plants/FamilyModal';
import { getPlantProfiles, createPlantProfile, deletePlantProfile } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf, faSeedling, faChevronRight, faPlus, faTrash, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

const PlantsPage = () => {
	const navigate = useNavigate();
	const { showToast } = useToast();
	const [profiles, setProfiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [isAddOpen, setIsAddOpen] = useState(false);
	const [creating, setCreating] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [confirmText, setConfirmText] = useState('');
	const [deleting, setDeleting] = useState(false);

	useEffect(() => {
		const fetchProfiles = async () => {
			try {
				const response = await getPlantProfiles();
				if (!response.ok) {
					throw new Error('Failed to fetch plant profiles');
				}
				const data = await response.json();
				setProfiles(data.data || []);
			} catch (err) {
				setError(err.message);
			} finally {
				setLoading(false);
			}
		};
		fetchProfiles();
	}, []);

	const handleCreate = async (familyName, description) => {
		try {
			setCreating(true);
			const response = await createPlantProfile({ family: familyName, description });
			const data = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(data.message || data.error?.message || 'Failed to add plant family');
			}
			showToast('success', 'Plant family added');
			setIsAddOpen(false);
			// Jump straight into the editor to configure the new family's profile.
			navigate(`/plants/${data.data.id}`);
		} catch (err) {
			showToast('error', err.message);
		} finally {
			setCreating(false);
		}
	};

	const openDeleteModal = (profile, e) => {
		e.stopPropagation();
		setDeleteTarget(profile);
		setConfirmText('');
	};

	const closeDeleteModal = () => {
		setDeleteTarget(null);
		setConfirmText('');
	};

	const handleDelete = async () => {
		if (!deleteTarget || confirmText !== deleteTarget.family) return;
		try {
			setDeleting(true);
			const response = await deletePlantProfile(deleteTarget.id);
			if (!response.ok) {
				const data = await response.json().catch(() => ({}));
				throw new Error(data.message || data.error?.message || 'Failed to delete plant family');
			}
			setProfiles((prev) => prev.filter((p) => p.id !== deleteTarget.id));
			showToast('success', 'Plant family deleted');
			closeDeleteModal();
		} catch (err) {
			showToast('error', err.message);
		} finally {
			setDeleting(false);
		}
	};

	// The general fallback profile is pinned above the families: it is not one of
	// them, it is what a device is graded against until it belongs to one.
	const general = profiles.find((p) => p.is_general);
	const families = profiles.filter((p) => !p.is_general);

	const content = () => {
		if (loading) return <p>Loading plant families...</p>;
		if (error) return <p className="text-red-500">Error: {error}</p>;
		if (!general && families.length === 0) {
			return (
				<div className="bg-white shadow-md rounded-lg p-8 text-center text-gray-500">
					No plant families yet. Add one with the button above, or they appear automatically when a plant is identified from a device image.
				</div>
			);
		}

		return (
			<div className="bg-white shadow-md rounded-lg divide-y divide-gray-100">
				{general && (
					<button
						onClick={() => navigate(`/plants/${general.id}`)}
						data-testid="plant-general-row"
						className="flex items-center gap-4 w-full px-5 py-4 text-left hover:bg-gray-50 transition-colors"
					>
						<div className="flex-shrink-0 w-11 h-11 bg-gray-100 rounded-xl flex items-center justify-center">
							<FontAwesomeIcon icon={faSeedling} className="text-gray-500" />
						</div>
						<div className="flex-1 min-w-0">
							<div data-testid="plant-general-family" className="font-semibold text-text-strong truncate">{general.family}</div>
							<div className="text-xs text-text-weak mt-0.5 truncate">
								{general.description || 'Fallback profile for devices with no identified plant family.'}
							</div>
						</div>
						<span className="inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 whitespace-nowrap">
							Fallback
						</span>
						<FontAwesomeIcon icon={faChevronRight} className="text-gray-300" />
					</button>
				)}
				{families.length === 0 && (
					<p className="px-5 py-4 text-sm text-gray-500">
						No plant families yet. Add one with the button above, or they appear automatically when a plant is identified from a device image.
					</p>
				)}
				{families.map((profile) => (
					<div key={profile.id} data-testid="plant-row" data-family={profile.family} className="flex items-center gap-2 px-5 py-4 hover:bg-gray-50 transition-colors">
						<button
							onClick={() => navigate(`/plants/${profile.id}`)}
							data-testid="plant-open"
							className="flex items-center gap-4 flex-1 min-w-0 text-left"
						>
							<div className="flex-shrink-0 w-11 h-11 bg-green-50 rounded-xl flex items-center justify-center">
								<FontAwesomeIcon icon={faLeaf} className="text-green-600" />
							</div>
							<div className="flex-1 min-w-0">
								<div data-testid="plant-family" className="font-semibold text-text-strong truncate">{profile.family}</div>
								{profile.description && (
									<div className="text-xs text-text-weak mt-0.5 truncate">{profile.description}</div>
								)}
							</div>
							{profile.configured ? (
								<span className="inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
									Configured
								</span>
							) : (
								<span className="inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
									Not configured
								</span>
							)}
							<FontAwesomeIcon icon={faChevronRight} className="text-gray-300" />
						</button>
						<button
							onClick={(e) => openDeleteModal(profile, e)}
							data-testid="plant-delete-open"
							className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
							title={`Delete ${profile.family}`}
						>
							<FontAwesomeIcon icon={faTrash} />
						</button>
					</div>
				))}
			</div>
		);
	};

	return (
		<div className="flex flex-col min-h-screen">
			<Header />
			<main className="flex-grow container mx-auto px-4 py-8">
				<div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
					<div>
						<h1 className="text-2xl font-bold">Plants</h1>
						<p className="text-sm text-text-weak mt-1">
							Plant family care profiles. Select a family to edit its ideal yearly profile for moisture, temperature, light and nutrition.
							Devices with no identified family are graded against <span className="font-medium">General</span>.
						</p>
					</div>
					<button
						onClick={() => setIsAddOpen(true)}
						data-testid="plant-add-open"
						className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap"
					>
						<FontAwesomeIcon icon={faPlus} />
						Add family
					</button>
				</div>
				{content()}
			</main>
			<Footer />

			<FamilyModal
				isOpen={isAddOpen}
				mode="add"
				submitting={creating}
				onClose={() => setIsAddOpen(false)}
				onSubmit={handleCreate}
			/>

			<Modal isOpen={!!deleteTarget} onClose={closeDeleteModal} title="Delete plant family">
				<div className="space-y-4">
					<div className="flex gap-3 bg-red-50 border border-red-100 rounded-md p-3">
						<FontAwesomeIcon icon={faTriangleExclamation} className="text-red-500 mt-0.5" />
						<p className="text-sm text-red-800">
							This permanently deletes <span className="font-semibold">{deleteTarget?.family}</span> and its care profile. Any devices linked to this family will be unlinked. This cannot be undone.
						</p>
					</div>
					<div>
						<label className="block text-sm text-gray-700 mb-1">
							Type <span className="font-mono font-semibold">{deleteTarget?.family}</span> to confirm
						</label>
						<input
							type="text"
							value={confirmText}
							onChange={(e) => setConfirmText(e.target.value)}
							data-testid="plant-delete-confirm-text"
							autoFocus
							onKeyDown={(e) => e.key === 'Enter' && confirmText === deleteTarget?.family && handleDelete()}
							className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
						/>
					</div>
					<div className="flex justify-end gap-2 mt-6">
						<button onClick={closeDeleteModal} disabled={deleting} data-testid="plant-delete-cancel" className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
							Cancel
						</button>
						<button
							onClick={handleDelete}
							data-testid="plant-delete-submit"
							disabled={deleting || confirmText !== deleteTarget?.family}
							className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
						>
							{deleting ? 'Deleting...' : 'Delete family'}
						</button>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default PlantsPage;
